import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { AlertTriangle, Check, Copy, KeyRound, Plus, RefreshCw, ShieldCheck } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { StatusBadge } from '@/components/StatusBadge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { api } from '@/lib/api';

// Max key name size in bytes (UTF-8) — matches docs/data-keys-spec.md.
const MAX_NAME_BYTES = 128;
// Recommended shape: lowercase kebab, e.g. `detector-v3`, `anpr-v2`, `generic`.
const KEBAB_RE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

// A single (product, name, version) row as returned by the server. The API
// NEVER returns key material — there is deliberately no field for it here.
interface RawDataKey {
  id?: string;
  name?: string;
  version?: number;
  currentVersion?: number;
  current_version?: number;
  status?: string;
  createdAt?: number | string;
  created_at?: number | string;
  rotatedAt?: number | string | null;
  rotated_at?: number | string | null;
  versions?: RawDataKey[];
}

// One row per key *name*, collapsing all of its versions.
interface DataKey {
  name: string;
  currentVersion: number;
  status: string;
  versionCount: number;
  createdAt?: number | string;
  rotatedAt?: number | string | null;
}

function byteLength(value: string): number {
  return new TextEncoder().encode(value).length;
}

function toDate(value?: number | string | null): Date | null {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'number') {
    // Tolerate both epoch seconds and epoch milliseconds.
    return new Date(value < 1e12 ? value * 1000 : value);
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function formatDate(value?: number | string | null): string {
  const date = toDate(value);
  return date ? format(date, 'MMM d, yyyy HH:mm') : '—';
}

// The list endpoint may return either one row per version or a grouped shape
// with a nested `versions` array. Normalise both into one row per name.
function normalizeKeys(rows: RawDataKey[]): DataKey[] {
  const flat: RawDataKey[] = [];
  for (const row of rows) {
    if (Array.isArray(row.versions) && row.versions.length > 0) {
      for (const v of row.versions) flat.push({ ...row, ...v, name: row.name ?? v.name });
    } else {
      flat.push(row);
    }
  }

  const byName = new Map<string, DataKey>();
  for (const row of flat) {
    const name = row.name;
    if (!name) continue;
    const version = Number(row.version ?? row.currentVersion ?? row.current_version ?? 1);
    const status = (row.status || 'ACTIVE').toUpperCase();
    const createdAt = row.createdAt ?? row.created_at;
    const rotatedAt = row.rotatedAt ?? row.rotated_at ?? null;

    const existing = byName.get(name);
    if (!existing) {
      byName.set(name, {
        name,
        currentVersion: version,
        status,
        versionCount: 1,
        createdAt,
        rotatedAt,
      });
      continue;
    }

    existing.versionCount += 1;
    // Newest version wins for the headline version/status/rotation.
    if (version >= existing.currentVersion) {
      existing.currentVersion = version;
      existing.status = status;
      existing.rotatedAt = rotatedAt ?? existing.rotatedAt;
    }
    // Oldest creation date is when the key name first appeared.
    const a = toDate(createdAt);
    const b = toDate(existing.createdAt);
    if (a && (!b || a < b)) existing.createdAt = createdAt;
  }

  return Array.from(byName.values()).sort((a, b) => a.name.localeCompare(b.name));
}

interface DataKeysDialogProps {
  product: { id: string; name: string } | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function DataKeysDialog({ product, open, onOpenChange }: DataKeysDialogProps) {
  const { t } = useTranslation();
  const [keys, setKeys] = useState<DataKey[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [isAddOpen, setIsAddOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);

  const [rotateTarget, setRotateTarget] = useState<DataKey | null>(null);
  const [isRotating, setIsRotating] = useState(false);

  const [copiedName, setCopiedName] = useState<string | null>(null);

  useEffect(() => {
    if (open && product) {
      fetchKeys(product.id);
    } else {
      setKeys([]);
      setError(null);
      setIsAddOpen(false);
      setNewName('');
      setAddError(null);
      setRotateTarget(null);
      setCopiedName(null);
    }
  }, [open, product?.id]);

  const fetchKeys = async (productId: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await api.get(`/api/products/${productId}/data-keys`);
      // The endpoint answers `{ success, data: { keys: [...] } }`; tolerate a
      // bare array too so the UI does not break if the envelope changes.
      const rows: RawDataKey[] = Array.isArray(res?.data)
        ? res.data
        : Array.isArray(res?.data?.keys)
          ? res.data.keys
          : Array.isArray(res?.keys)
            ? res.keys
            : [];
      setKeys(normalizeKeys(rows));
    } catch (err: any) {
      setError(err?.message || 'Failed to load data keys');
      setKeys([]);
    } finally {
      setIsLoading(false);
    }
  };

  const trimmedName = newName.trim();
  const nameBytes = byteLength(trimmedName);
  const isDuplicate = keys.some((k) => k.name === trimmedName);
  const nameValid = trimmedName.length > 0 && nameBytes <= MAX_NAME_BYTES && !isDuplicate;
  const nameHint = (() => {
    if (!trimmedName) return null;
    if (isDuplicate) return `A key named "${trimmedName}" already exists — rotate it instead.`;
    if (nameBytes > MAX_NAME_BYTES) return `Too long: ${nameBytes} bytes (max ${MAX_NAME_BYTES}).`;
    if (!KEBAB_RE.test(trimmedName)) return 'Tip: lowercase kebab-case reads best, e.g. detector-v3 or generic.';
    return null;
  })();

  const handleAddKey = async () => {
    if (!product || !nameValid) return;
    setIsSaving(true);
    setAddError(null);
    try {
      await api.post(`/api/products/${product.id}/data-keys`, { name: trimmedName });
      setIsAddOpen(false);
      setNewName('');
      await fetchKeys(product.id);
    } catch (err: any) {
      setAddError(err?.message || 'Failed to create data key');
    } finally {
      setIsSaving(false);
    }
  };

  const handleRotate = async () => {
    if (!product || !rotateTarget) return;
    setIsRotating(true);
    setError(null);
    try {
      await api.post(
        `/api/products/${product.id}/data-keys/${encodeURIComponent(rotateTarget.name)}/rotate`,
      );
      setRotateTarget(null);
      await fetchKeys(product.id);
    } catch (err: any) {
      setError(err?.message || 'Failed to rotate data key');
      setRotateTarget(null);
    } finally {
      setIsRotating(false);
    }
  };

  const copyEncryptCommand = async (key: DataKey) => {
    if (!product) return;
    const command = `airgap encrypt --product ${product.id} --key ${key.name} <files...>`;
    try {
      await navigator.clipboard.writeText(command);
      setCopiedName(key.name);
      setTimeout(() => setCopiedName((current) => (current === key.name ? null : current)), 2000);
    } catch (err) {
      console.error('Failed to copy encrypt command:', err);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <KeyRound className="h-5 w-5 text-primary" />
              Data keys{product ? ` — ${product.name}` : ''}
            </DialogTitle>
            <DialogDescription>
              Named encryption keys delivered to licensed devices inside the signed license. The
              build pipeline encrypts artifacts with a key name; the app asks the SDK to decrypt
              with the same name at runtime.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="rounded-md border bg-muted/40 p-4 text-sm text-muted-foreground space-y-2">
              <p>
                The key name is the contract between your build and your app: encrypt with{' '}
                <code className="font-mono">airgap encrypt --key &lt;name&gt;</code>, then ask the
                SDK to decrypt with that same name. Names are opaque UTF-8, max {MAX_NAME_BYTES}{' '}
                bytes — lowercase kebab like <code className="font-mono">detector-v3</code> or{' '}
                <code className="font-mono">generic</code> is the convention.
              </p>
              <p className="flex items-start gap-2">
                <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                <span>
                  Key material is never shown here and never leaves the server for the portal — the
                  raw key is only released to the build pipeline via an audited, API-key
                  authenticated endpoint.
                </span>
              </p>
              <p className="flex items-start gap-2">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
                <span>
                  This binds encrypted artifacts to licensed hardware and deters copying between
                  machines. It is deterrence, not absolute DRM: an attacker with root on the device
                  can extract the seed from the SDK binary and dump plaintext from memory.
                </span>
              </p>
            </div>

            <div className="flex items-center justify-between">
              <div className="text-sm text-muted-foreground">
                {isLoading ? t('common.loading') : `${keys.length} key${keys.length === 1 ? '' : 's'}`}
              </div>
              <Button size="sm" onClick={() => { setNewName(''); setAddError(null); setIsAddOpen(true); }} disabled={!product}>
                <Plus className="h-4 w-4 mr-2" />
                Add key
              </Button>
            </div>

            {error && (
              <div className="rounded-md border border-destructive/50 p-3 text-sm text-destructive">
                {error}
              </div>
            )}

            {isLoading ? (
              <div className="py-8 text-center text-sm text-muted-foreground">
                {t('common.loading')}
              </div>
            ) : keys.length === 0 && !error ? (
              <div className="rounded-md border border-dashed py-10 text-center text-sm text-muted-foreground">
                No data keys yet. Add one to start shipping encrypted artifacts with this product.
              </div>
            ) : keys.length > 0 ? (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Version</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead>Rotated</TableHead>
                      <TableHead className="text-right">{t('users.actions')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {keys.map((key) => (
                      <TableRow key={key.name}>
                        <TableCell className="font-mono font-medium">{key.name}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Badge variant="outline">v{key.currentVersion}</Badge>
                            {key.versionCount > 1 && (
                              <span className="text-xs text-muted-foreground">
                                +{key.versionCount - 1} retained
                              </span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <StatusBadge status={key.status} />
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {formatDate(key.createdAt)}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {formatDate(key.rotatedAt)}
                        </TableCell>
                        <TableCell className="text-right whitespace-nowrap">
                          <Button variant="ghost" size="sm" onClick={() => copyEncryptCommand(key)}>
                            {copiedName === key.name ? (
                              <Check className="h-4 w-4 mr-2" />
                            ) : (
                              <Copy className="h-4 w-4 mr-2" />
                            )}
                            Copy command
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => setRotateTarget(key)}>
                            <RefreshCw className="h-4 w-4 mr-2" />
                            Rotate
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : null}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              {t('common.close')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add key dialog */}
      <Dialog open={isAddOpen} onOpenChange={setIsAddOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add data key</DialogTitle>
            <DialogDescription>
              Mints a new 32-byte key at version 1. The key material stays on the server — you only
              choose the name.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="data-key-name">Key name</Label>
              <Input
                id="data-key-name"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="detector-v3"
                className="font-mono"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && nameValid && !isSaving) handleAddKey();
                }}
              />
              <p className="text-xs text-muted-foreground">
                Lowercase kebab-case is the convention, e.g.{' '}
                <code className="font-mono">detector-v3</code>,{' '}
                <code className="font-mono">anpr-v2</code>, or{' '}
                <code className="font-mono">generic</code> (the fallback the SDK uses when a
                requested name is missing). Max {MAX_NAME_BYTES} bytes
                {trimmedName ? ` — currently ${nameBytes}` : ''}.
              </p>
              {nameHint && (
                <p
                  className={
                    isDuplicate || nameBytes > MAX_NAME_BYTES
                      ? 'text-xs font-medium text-destructive'
                      : 'text-xs text-amber-600 dark:text-amber-400'
                  }
                >
                  {nameHint}
                </p>
              )}
            </div>
            {addError && <div className="text-sm font-medium text-destructive">{addError}</div>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddOpen(false)} disabled={isSaving}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleAddKey} disabled={!nameValid || isSaving}>
              {isSaving ? t('common.loading') : 'Create key'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rotate confirmation */}
      <AlertDialog
        open={rotateTarget !== null}
        onOpenChange={(next) => { if (!next) setRotateTarget(null); }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Rotate “{rotateTarget?.name}” to v{(rotateTarget?.currentVersion ?? 0) + 1}?
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2">
                <p>
                  Artifacts already encrypted with v{rotateTarget?.currentVersion} keep working:
                  the previous version is retained (marked RETIRED) and is still shipped inside
                  newly issued licenses, so devices can decrypt older files.
                </p>
                <p>
                  Every <strong>new</strong> encryption will use v
                  {(rotateTarget?.currentVersion ?? 0) + 1}. Devices need a re-issued license
                  before they can decrypt artifacts made with the new version.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isRotating}>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); handleRotate(); }}
              disabled={isRotating}
            >
              {isRotating ? t('common.loading') : 'Rotate key'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

export default DataKeysDialog;
